import {
    ButtonHTMLAttributes,
    createContext,
    DetailedHTMLProps,
    PropsWithChildren,
    ReactNode,
    useContext,
} from 'react';
import classNames from 'classnames';

import { Spinner } from './Spinner';

import styles from './Button.module.scss';

type HtmlButtonProps = DetailedHTMLProps<
    ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
>;

interface ButtonGroupContextData {
    color?: ButtonProps['color'];
    size?: ButtonProps['size'];
}

const ButtonGroupContext = createContext<ButtonGroupContextData>({});

export interface ButtonGroupProps extends PropsWithChildren {
    color?: ButtonProps['color'];
    size?: ButtonProps['size'];
    className?: string;
}

export const ButtonGroup = (props: ButtonGroupProps) => {
    return (
        <ButtonGroupContext.Provider
            value={{
                color: props.color,
                size: props.size,
            }}
        >
            <div
                className={classNames(styles.group, props.className)}
                role="group"
            >
                {props.children}
            </div>
        </ButtonGroupContext.Provider>
    );
};

export type ButtonProps = {
    color?: 'default' | 'primary' | 'secondary' | 'positive' | 'danger';
    size?: 'large' | 'small' | 'medium';
    loading?: boolean;
    startContent?: ReactNode;
} & HtmlButtonProps;

export const Button = (props: ButtonProps) => {
    const groupContext = useContext(ButtonGroupContext);
    const {
        color = groupContext.color,
        size = groupContext.size,
        loading: isLoading,
        disabled: isDisabled,
        startContent,
        children,
        className,
        ...buttonProps
    } = props;

    return (
        <button
            disabled={(isLoading && isDisabled !== false) || isDisabled}
            {...buttonProps}
            className={classNames(styles.container, color, size, className)}
        >
            {isLoading && <Spinner />}
            {startContent}
            {children}
        </button>
    );
};
